import axios from 'axios';
import { syncService } from './syncService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add auth token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface Idea {
  id: number;
  content: string;
  created_at: string;
  updated_at: string;
  processed: boolean;
  category?: string;
  priority?: number;
  tags?: string;
  summary?: string;
  expanded_content?: string;
  audio_path?: string;
  transcription_status?: string;
}

export interface SearchParams {
  query?: string;
  category?: string;
  priority?: number;
  startDate?: string;
  endDate?: string;
  hasAudio?: boolean;
  processed?: boolean;
  page?: number;
  limit?: number;
}

export interface SearchResponse {
  ideas: Idea[];
  total: number;
  page: number;
  totalPages: number;
}

export interface Project {
  id: number;
  name: string;
  description?: string;
  created_at: string;
  updated_at: string;
  priority: number;
  status: string;
}

export const ideaApi = {
  getAll: async (): Promise<Idea[]> => {
    const response = await api.get('/ideas');
    // Backend returns { ideas: [...], pagination: {...} }
    return response.data.ideas || response.data;
  },

  getById: async (id: number): Promise<Idea> => {
    const response = await api.get(`/ideas/${id}`);
    return response.data;
  },

  search: async (params: SearchParams): Promise<SearchResponse> => {
    const response = await api.get('/search', { params });
    return response.data;
  },

  create: async (content: string): Promise<Idea> => {
    const response = await api.post('/ideas', { content });
    // Emit sync event for real-time updates
    syncService.emitToServer('idea:create', response.data);
    return response.data;
  },

  createVoiceNote: async (audioBlob: Blob): Promise<Idea> => {
    const formData = new FormData();
    formData.append('audio', audioBlob, 'recording.webm');
    
    const response = await api.post('/ideas/voice', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  update: async (id: number, data: Partial<Idea>): Promise<Idea> => {
    const response = await api.put(`/ideas/${id}`, data);
    // Emit sync event for real-time updates
    syncService.emitToServer('idea:update', response.data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/ideas/${id}`);
    // Emit sync event for real-time updates
    syncService.emitToServer('idea:delete', { id });
  },

  exportToMarkdown: async (params?: SearchParams): Promise<Blob> => {
    const response = await api.get('/export/markdown', {
      params,
      responseType: 'blob'
    });
    return response.data;
  },

  exportToJson: async (params?: SearchParams): Promise<Blob> => {
    const response = await api.get('/export/json', {
      params,
      responseType: 'blob'
    });
    return response.data;
  },

  getCategories: async (): Promise<string[]> => {
    const response = await api.get('/search/categories');
    return response.data;
  },

  getLinkedIdeas: async (id: number): Promise<any[]> => {
    const response = await api.get(`/ideas/${id}/links`);
    return response.data;
  },

  getActionItems: async (): Promise<any[]> => {
    const response = await api.get('/actions');
    return response.data;
  },

  getIdeaActionItems: async (ideaId: number): Promise<any[]> => {
    const response = await api.get(`/ideas/${ideaId}/actions`);
    return response.data;
  },

  updateActionItem: async (id: number, updates: any): Promise<void> => {
    await api.put(`/actions/${id}`, updates);
  },
};

export const projectApi = {
  getAll: async (): Promise<Project[]> => {
    const response = await api.get('/projects');
    return response.data;
  },

  create: async (data: Partial<Project>): Promise<Project> => {
    const response = await api.post('/projects', data);
    return response.data;
  },
};

export const authApi = {
  login: async (password: string): Promise<{ token: string }> => {
    const response = await api.post('/auth/login', { password });
    return response.data;
  },

  verify: async (): Promise<{ valid: boolean }> => {
    const response = await api.get('/auth/verify');
    return response.data;
  },
};

export interface ApiKey {
  id: number;
  key_prefix: string;
  name: string;
  created_at: string;
  last_used_at?: string;
  is_active: boolean;
}

export interface ApiKeyGenerated {
  id: number;
  key: string;
  key_prefix: string;
  name: string;
  created_at: string;
}

export const settingsApi = {
  getApiKeys: async (): Promise<ApiKey[]> => {
    const response = await api.get('/settings/api-keys');
    return response.data;
  },

  generateApiKey: async (name?: string): Promise<ApiKeyGenerated> => {
    const response = await api.post('/settings/api-keys', { name });
    return response.data;
  },

  regenerateApiKey: async (name?: string): Promise<ApiKeyGenerated> => {
    const response = await api.post('/settings/api-keys/regenerate', { name });
    return response.data;
  },

  deleteApiKey: async (id: number): Promise<void> => {
    await api.delete(`/settings/api-keys/${id}`);
  },
};

export { api as default };